import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "hooks";
import { moveProjectThunk } from "containers/project-board/project-board-container.slice";

interface MoveCardSheetProps {
  cardId: string;
  companyId: string;
  onClose: () => void;
}

/**
 * Moves a card to another list without dragging, for a phone where a drag is
 * hard to finish: the board's lists in order, a tap puts the card at the top of
 * the chosen list (the same move a drop makes).
 */
export default function MoveCardSheet({ cardId, companyId, onClose }: MoveCardSheetProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const columnOrder = useAppSelector((s) => s.projectBoardReducer.columnOrder);
  const columnsById = useAppSelector((s) => s.projectBoardReducer.columnsById);
  const currentColumnId = useAppSelector(
    (s) => s.projectBoardReducer.cardsById[cardId]?.projectBoardColumn
  );

  // A list that is not saved yet has a temporary id the server does not know.
  const savedColumnIds = columnOrder.filter((columnId) => !columnId.startsWith("temp"));

  const moveTo = (columnId: string) => {
    onClose();
    dispatch(moveProjectThunk({ companyId, cardId, toColumn: columnId, toIndex: 0 }));
  };

  return (
    <Dialog open onClose={onClose} className="relative z-[1000]">
      <DialogBackdrop className="fixed inset-0 bg-zinc-950/50" />
      <div className="fixed inset-x-0 bottom-0 flex">
        <DialogPanel className="w-full rounded-t-xl bg-white pb-[env(safe-area-inset-bottom)] shadow-xl">
          <DialogTitle className="border-b border-gray-200 px-4 py-3 text-base font-semibold text-gray-900">
            {t("move", { defaultValue: "Move" })}
          </DialogTitle>
          <ul className="max-h-[60dvh] overflow-y-auto">
            {savedColumnIds.map((columnId) => {
              const isCurrent = columnId === currentColumnId;
              return (
                <li key={columnId}>
                  <button
                    type="button"
                    disabled={isCurrent}
                    onClick={() => moveTo(columnId)}
                    className="flex min-h-12 w-full items-center justify-between gap-3 px-4 text-left text-sm text-gray-900 active:bg-gray-100 disabled:text-gray-400"
                  >
                    <span className="min-w-0 break-words">{columnsById[columnId]?.name}</span>
                    {isCurrent && <Check aria-hidden size={16} className="shrink-0" />}
                  </button>
                </li>
              );
            })}
          </ul>
          <button
            type="button"
            onClick={onClose}
            className="min-h-12 w-full border-t border-gray-200 px-4 text-sm font-medium text-gray-700"
          >
            {t("cancel", { defaultValue: "Cancel" })}
          </button>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
